import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import History from './history';
import { Link } from 'react-router-dom';
import ModalVideo from 'react-modal-video';
import useFetch from 'core/hooks/api/useFetch';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import ImageWithBaseApi from 'components/common/mediaRender/ImageWithBaseApi';
import './about-content.css';
import { Helmet } from 'react-helmet';

const extractVideoId = (url) => {
    try {
        const urlParams = new URLSearchParams(new URL(url).search);
        return urlParams.get('v');
    } catch (error) {
        return null;
    }
};

const Placeholder = () => (
    <div className="placeholder container">
        <div className="row">
            <div className="col-md-6">
                <div className="placeholder-image" />
            </div>
            <div className="col-md-6 gap-2 d-flex flex-column">
                <div className="placeholder-text" />
                <div className="placeholder-text short" />
                <div className="placeholder-text-show" />
                <div className="placeholder-text short" />
            </div>
        </div>
    </div>
);

const AboutContent = ({ data, preview=true }) => {
    const { data: fetchedData, loading, error } = useFetch(API_ENDPOINTS.ABOUT);
    const [isOpen, setIsOpen] = useState(false);
    const [aboutData, setAboutData] = useState(data);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!data && fetchedData) {
            setAboutData(fetchedData);
            setIsLoading(false);
        }
    }, [data, fetchedData]);

    useEffect(() => {
        if (loading) {
            setIsLoading(true);
        }
    }, [loading]);

    const openModal = () => {
        setIsOpen(true);
    };

    const renderButtonLink = (linkText) => {
        if (!linkText) return null;
        const isExternal = linkText.startsWith('http://') || linkText.startsWith('https://');
        return isExternal ? (
            <a href={linkText} className="theme-btn-1 btn btn-effect-1" target="_blank" rel="noopener noreferrer">
                Ver más
            </a>
        ) : (
            <Link to={linkText} className="theme-btn-1 btn btn-effect-1">
                Ver más
            </Link>
        );
    };

    if (preview && loading ) return (
        <div className="loading-placeholder">
            <Placeholder />
        </div>
    );
    if (error) return <p>Error: {error.message}</p>;

    const videoId = aboutData?.videoId ? extractVideoId(aboutData?.videoId) : null;
    const videoThumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';

    return (
        <>
            <Helmet>
                <title>Artectum: Expertos en Acabados para Construcción en Perú</title>
                <meta name="description" content="Meta Descripción: Más de [X] años fabricando baldosas, mosaicos y celosías de alta calidad. Innovación y durabilidad para cada proyecto. Escríbenos por WhatsApp." />
            </Helmet>
            <div className="ltn__about-us-area pt-120--- pb-90 mt-5 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 align-self-center">
                            <div className="about-us-img-wrap about-img-left">
                                <ImageWithBaseApi src={aboutData?.image} alt="Acerca de Actectum" />
                                {videoId && (
                                    <div className="about-us-img-info about-us-img-info-2 about-us-img-info-3">
                                        <div className="ltn__video-img ltn__animation-pulse1">
                                            <img className="video-thumbnail" src={videoThumbnail} alt="Miniatura de video" />
                                            <Link className="ltn__video-icon-2 ltn__video-icon-2-border---" to="#" onClick={openModal}>
                                                <i className="fa fa-play" />
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-6 align-self-center">
                            <div className="about-us-info-wrap">
                                <div className="section-title-area ltn__section-title-2--- mb-20">
                                    <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">{aboutData?.subtitle}</h6>
                                    <h1 className="section-title">{aboutData?.title}<span>.</span></h1>
                                    <p style={{ textAlign: 'justify' }}>{aboutData?.description}</p>
                                </div>
                                <ul className="ltn__list-item-half clearfix">
                                    {aboutData?.features.map((feature, index) => (
                                        <li key={index}>
                                            <i className={feature.icon} />
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>
                                <div className="ltn__callout bg-overlay-theme-05  mt-30">
                                    <p>{aboutData?.callout}</p>
                                </div>
                                <div className="btn-wrapper animated">
                                    {renderButtonLink(aboutData?.buttonText)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {videoId && (
                    <ModalVideo channel='youtube' isOpen={isOpen} videoId={videoId} onClose={() => setIsOpen(false)} />
                )}
                <History historyData={aboutData?.history} />
            </div>
        </>
    );
};

AboutContent.propTypes = {
    data: PropTypes.object
};

export default AboutContent;